import _ from 'lodash';

import {
  OrderState,
  QueryOrdersFilterInput,
  useSalesOrderActivity_TableLazyQuery
} from 'generated/graphql';

import Big from 'big.js';
import { listQueryUtil } from 'iron-forge-react-modules';
import { DateTime } from 'luxon';
import { NetSuiteRecordChip } from 'modules';
import * as sharedTypes from '../types';
import * as types from './types';



const useListQuery = listQueryUtil.genUseListQuery<types.IColumnId>();



export interface ISalesOrder {
  id: string;
  orderNumber: string;
  orderDate: DateTime;
  totalCents: Big;
  storeId: string;
  storeName: string;
  customerEmail: string;
  e1RecordUrl: string;
  netSuiteCustomerRecord: NetSuiteRecordChip.IWriteStatus;
  netSuiteSalesOrderRecord: NetSuiteRecordChip.IWriteStatus;
  trackingNumber: string;
  trackingUrl: string;
  totalQuantity: number;
  paymentMethod: string;
  netSuiteWriteStatus: NetSuiteRecordChip.IWriteStatus;
}

export interface IData {
  rows: ISalesOrder[];
}

export function useQuery(defaultFilter: sharedTypes.IFilter) {
  return useListQuery({
    useLazyQuery: useSalesOrderActivity_TableLazyQuery,
    clientColumn2GqlSortField: {
      orderDate: 'orderDate',
      orderNumber: 'orderNumber'
    },
    defaultFilterArgs: defaultFilter,
    genFilter2GqlFilter: defaultFilterArgs => (filter?: sharedTypes.IFilter): QueryOrdersFilterInput => ({
      after: filter?.after.toISO() ?? defaultFilter.after.toISO(),
      salesChannel: filter?.salesChannel ?? defaultFilterArgs.salesChannel,
      storeId: filter?.storeId ?? defaultFilterArgs.storeId,
      inState: __getState(defaultFilter, filter),
      omniFreeText: filter?.searchText
    }),
    initQuerySort: {
      columnId: 'orderDate',
      direction: 'descending'
    },
    gqlResultData2GqlPageInfo: gqlData => gqlData?.orders.pageInfo,
    gqlResultData2TupleData: (gqlData): IData => ({
      rows: _.map(gqlData.data?.orders.nodes ?? [], (node): ISalesOrder => ({
        id: node.id,
        storeId: node.store.id,
        orderDate: DateTime.fromISO(node.orderDate),
        orderNumber: node.orderNumber,
        customerEmail: node.customer.email,
        e1RecordUrl: node.e1DocumentIndex?.urlToE1Record ?? '',
        netSuiteCustomerRecord: NetSuiteRecordChip.genWriteStatus({
          netSuiteRecord: node.customer.netSuiteRecord,
          writeRequest: node.customer.netSuiteWriteRequest
        }),
        netSuiteSalesOrderRecord: NetSuiteRecordChip.genWriteStatus({
          netSuiteRecord: node.netSuiteRecord,
          writeRequest: node.netSuiteWriteRequest
        }),
        totalCents: new Big(node.totalCents),
        trackingNumber: node.shipment?.trackingNumber ?? '',
        trackingUrl: node.shipment?.trackingUrl ?? '',
        netSuiteWriteStatus: NetSuiteRecordChip.genWriteStatus({
          netSuiteRecord: node.itemFulfillment?.netSuiteRecord
        }),
        totalQuantity: _.sumBy(node.lineItems, li => li.quantity),
        paymentMethod: node.netSuitePaymentMethod?.name ?? node.paymentMethod.displayName,
        storeName: node.store.name
      }))
    })
  });
}



function __getState(defaultFilter: sharedTypes.IFilter, currentFilter?: sharedTypes.IFilter): OrderState {
  if (currentFilter) {
    return __getStateForTabId(currentFilter.tabId);
  }
  return __getStateForTabId(defaultFilter.tabId);
}



function __getStateForTabId(tabId: sharedTypes.IFilter['tabId']) {
  return tabId === 'newSalesOrders' ? 'awaitingApproval' : tabId;
}
